import type { BusinessSegmentConditions, SegmentConditionGroups } from '@reach/shared-types/conditions';
import { Search } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { SearchableSelect } from '@reach/web-ui/searchable-select';
import { isLegacySegmentFormat, type PreviewRecipient } from '../segments-tab/segment-definitions-api';
import { usePreviewSegmentRecipients, useTenants } from '../segments-tab/useSegmentTemplates';
import styles from './EditSegment.module.css';
import { RecipientCard } from './RecipientCard';

const PAGE_SIZE = 20;

interface PreviewRequest {
  tenantId: string;
  conditions?: BusinessSegmentConditions;
  conditionGroups?: SegmentConditionGroups;
}

interface RecipientsPreviewProps {
  conditions?: BusinessSegmentConditions | null;
  conditionGroups?: SegmentConditionGroups | null;
  isJsonValid: boolean;
}

export const RecipientsPreview: React.FC<RecipientsPreviewProps> = ({
  conditions,
  conditionGroups,
  isJsonValid,
}) => {
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [recipients, setRecipients] = useState<PreviewRecipient[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [previewRequest, setPreviewRequest] = useState<PreviewRequest | null>(null);
  const [nextCursor, setNextCursor] = useState<string | undefined>();

  const { data: tenants = [], isLoading: isLoadingTenants } = useTenants();
  const { mutate: previewRecipients, isPending: isValidating } = usePreviewSegmentRecipients();
  const { mutate: previewNextPage, isPending: isLoadingMore } = usePreviewSegmentRecipients();

  const tenantOptions = useMemo(
    () => tenants.map(t => ({ id: t.id, label: t.name || t.id })),
    [tenants],
  );

  const handleTenantChange = (tenantId: string) => {
    setSelectedTenantId(tenantId);
    setRecipients([]);
    setTotal(null);
    setPreviewRequest(null);
    setNextCursor(undefined);
  };

  const isLegacyFormat = isLegacySegmentFormat({ conditions, conditionGroups });
  const hasPreviewPayload = isLegacyFormat
    ? (conditions?.length ?? 0) > 0
    : (conditionGroups?.length ?? 0) > 0;

  const handleValidate = () => {
    if (!selectedTenantId || !hasPreviewPayload || !isJsonValid) return;

    const request: PreviewRequest = isLegacyFormat
      ? { tenantId: selectedTenantId, conditions: conditions ?? undefined }
      : { tenantId: selectedTenantId, conditionGroups: conditionGroups ?? undefined };

    previewRecipients(
      { ...request, limit: PAGE_SIZE },
      {
        onSuccess: data => {
          setPreviewRequest(request);
          setRecipients(data.recipients);
          setTotal(data.estimatedTotal ?? data.recipients.length);
          setNextCursor(data.pagination.cursor);
        },
      },
    );
  };

  const handleLoadMore = () => {
    if (!previewRequest || !nextCursor) return;

    previewNextPage(
      { ...previewRequest, limit: PAGE_SIZE, cursor: nextCursor },
      {
        onSuccess: data => {
          setRecipients(previous => [...previous, ...data.recipients]);
          setNextCursor(data.pagination.cursor);
        },
      },
    );
  };

  const canValidate = selectedTenantId && hasPreviewPayload && isJsonValid;

  return (
    <aside className={styles.preview}>
      <header>
        <h3>Recipients Preview</h3>
      </header>

      <section className={styles.controls}>
        <div>
          <label htmlFor="tenant-select">Validate Against Tenant</label>
          <SearchableSelect
            options={tenantOptions}
            value={selectedTenantId}
            onChange={handleTenantChange}
            placeholder="Select a tenant..."
            searchPlaceholder="Search tenants..."
            isLoading={isLoadingTenants}
            loadingText="Loading tenants..."
            emptyText="No tenants found"
          />
        </div>

        <Button onClick={handleValidate} disabled={!canValidate || isValidating || isLoadingMore}>
          {isValidating ? 'Validating...' : 'Validate Segment'}
        </Button>
      </section>

      <section className={styles.results}>
        <RecipientsResults
          recipients={recipients}
          total={total}
          hasMore={nextCursor !== undefined}
          isLoadingMore={isLoadingMore || isValidating}
          onLoadMore={handleLoadMore}
        />
      </section>
    </aside>
  );
};

interface RecipientsResultsProps {
  recipients: PreviewRecipient[];
  total: number | null;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
}

function RecipientsResults({
  recipients,
  total,
  hasMore,
  isLoadingMore,
  onLoadMore,
}: RecipientsResultsProps): React.ReactElement {
  if (total === null) {
    return (
      <figure className={styles.emptyState}>
        <Search />
        <p>Select a tenant and click validate to preview recipients</p>
      </figure>
    );
  }

  if (recipients.length === 0) {
    return <p className={styles.noResults}>No matching recipients found</p>;
  }

  return (
    <>
      <p className={styles.resultsSummary}>
        <strong>{total}</strong> matching recipient{total !== 1 ? 's' : ''}
      </p>
      <ul className={styles.recipientList}>
        {recipients.map(recipient => (
          <RecipientCard key={recipient.id} recipient={recipient} />
        ))}
      </ul>
      {total > recipients.length && (
        <p className={styles.showingCount}>
          Showing {recipients.length} of {total}
        </p>
      )}
      {hasMore && (
        <Button
          variant="outline"
          onClick={onLoadMore}
          disabled={isLoadingMore}
          className={styles.loadMore}>
          {isLoadingMore ? 'Loading...' : 'Load more'}
        </Button>
      )}
    </>
  );
}
